import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import { getStoredUser, hasPermission } from "../../utils/permissions";
import type {
  BankAccount,
  FinanceDepartment,
  PayrollEmployee,
  PayrollEmployeeList,
  PayrollLine,
  PayrollRegisterReport,
  PayrollRunDetail,
  PayrollRunList,
  PayrollSettings,
  PayrollStatutory,
  RemittanceKind,
} from "../../types";
import { StatusTag } from "./VouchersPage";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/* ------------------------------------------------------------
 * Small conversions shared by every tab.
 *
 * A Postgres DATE arrives here as an instant (`2030-01-01T00:00:00Z` for a
 * Colombo-stored 1 January), so slicing the string would hand the operator
 * 31 December. Take the LOCAL day of the instant instead.
 * ------------------------------------------------------------ */
const dateKey = (value?: string | null): string => {
  if (!value) return "";
  if (!value.includes("T")) return value.slice(0, 10);
  const d = new Date(value);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const monthKey = (value?: string | null): string => dateKey(value).slice(0, 7);

const isNullish = (value: any): boolean => value === null || value === undefined;

/**
 * A salary nobody has entered is a question, not a figure. It is rendered as
 * a tag — `formatLkr` would turn the null into a confident "LKR 0", which is
 * a salary the resort does not pay anybody.
 */
const Salary: React.FC<{ value: number | null }> = ({ value }) =>
  isNullish(value) ? (
    <Tag>Not configured</Tag>
  ) : (
    <Text>{formatLkr(value as number)}</Text>
  );

const PAYMENT_OPTIONS = [
  { value: "credit", label: "On credit — owed to the employees, settled later" },
  { value: "bank_transfer", label: "Paid from a bank account now" },
  { value: "cash", label: "Paid from cash now" },
];

const fail = (e: any, fallback: string) => message.error(e?.message || fallback);

/**
 * Payroll — Income & Expense Phase 5.
 *
 * The runs are ordinary expense vouchers and are presented that way: the
 * same statuses, the same submit → approve → pay chain, the same void that
 * posts a reversal. What this page adds is the three facts a payroll
 * document carries that a purchase invoice does not — who is being paid, what
 * was withheld from them, and what that leaves the resort owing the EPF
 * department, the ETF board and the IRD.
 *
 * Two rules the UI has to keep:
 *   1. an unconfigured salary renders as "Not configured", never as 0;
 *   2. everything on this page sits behind `payroll:*` — a cashier holding
 *      `finance:view` reaches the P&L and does not reach a payslip.
 */
const PayrollPage: React.FC = () => {
  const canEdit = hasPermission("payroll:edit");
  const canApprove = hasPermission("finance:approve");
  /** The server refuses self-approval outright, so do not offer the button. */
  const myId = getStoredUser()?.id;

  /* ---- runs ---- */
  const [tab, setTab] = useState("runs");
  const [runs, setRuns] = useState<PayrollRunList | null>(null);
  const [status, setStatus] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm] = Form.useForm();
  /** Who this run will pay, and each person's allowance for the month. */
  const [selectedStaff, setSelectedStaff] = useState<string[]>([]);
  const [runAllowances, setRunAllowances] = useState<Record<string, number>>({});

  const [detail, setDetail] = useState<PayrollRunDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draftLines, setDraftLines] = useState<Array<{ staff_id: string; allowances: number }>>([]);

  const [payFor, setPayFor] = useState<PayrollRunDetail | null>(null);
  const [payBank, setPayBank] = useState<string | undefined>();
  const [voidFor, setVoidFor] = useState<PayrollRunDetail | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [rejectFor, setRejectFor] = useState<PayrollRunDetail | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [slipBusy, setSlipBusy] = useState(false);

  /* ---- employees ---- */
  const [employeeData, setEmployeeData] = useState<PayrollEmployeeList | null>(null);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  const [taxYear, setTaxYear] = useState<number>(dayjs().year());
  const [departments, setDepartments] = useState<FinanceDepartment[]>([]);
  const [profileFor, setProfileFor] = useState<PayrollEmployee | null>(null);
  const [profileForm] = Form.useForm();
  const [savingProfile, setSavingProfile] = useState(false);

  /* ---- statutory ---- */
  const [statutory, setStatutory] = useState<PayrollStatutory | null>(null);
  const [settings, setSettings] = useState<PayrollSettings | null>(null);
  const [statutoryLoading, setStatutoryLoading] = useState(false);
  const [statutoryError, setStatutoryError] = useState<string | null>(null);
  const [remitFor, setRemitFor] = useState<RemittanceKind | null>(null);
  const [remitForm] = Form.useForm();
  const [remitting, setRemitting] = useState(false);
  const [banks, setBanks] = useState<BankAccount[]>([]);

  /* ---- register ---- */
  const [regRange, setRegRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(29, "day"),
    dayjs(),
  ]);
  const [register, setRegister] = useState<PayrollRegisterReport | null>(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    setRunsError(null);
    try {
      setRuns(
        await financeService.getPayrollRuns({
          status,
          from: range ? range[0].format("YYYY-MM-DD") : undefined,
          to: range ? range[1].format("YYYY-MM-DD") : undefined,
          q: query || undefined,
          page,
          limit: 20,
        }),
      );
    } catch (e: any) {
      setRunsError(e?.message || "Failed to load payroll runs");
    } finally {
      setRunsLoading(false);
    }
  }, [status, range, query, page]);

  const loadEmployees = useCallback(async () => {
    setEmployeesLoading(true);
    setEmployeesError(null);
    try {
      setEmployeeData(await financeService.getPayrollEmployees(taxYear));
    } catch (e: any) {
      setEmployeesError(e?.message || "Failed to load employees");
    } finally {
      setEmployeesLoading(false);
    }
  }, [taxYear]);

  const loadStatutory = useCallback(async () => {
    setStatutoryLoading(true);
    setStatutoryError(null);
    try {
      setStatutory(await financeService.getStatutory());
    } catch (e: any) {
      setStatutoryError(e?.message || "Failed to load the statutory position");
    } finally {
      setStatutoryLoading(false);
    }
  }, []);

  const loadRegister = useCallback(async () => {
    setRegisterLoading(true);
    setRegisterError(null);
    try {
      setRegister(
        await financeService.getPayrollRegister({
          from: regRange[0].format("YYYY-MM-DD"),
          to: regRange[1].format("YYYY-MM-DD"),
        }),
      );
    } catch (e: any) {
      setRegisterError(e?.message || "Failed to load the payroll register");
    } finally {
      setRegisterLoading(false);
    }
  }, [regRange]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  /* Each tab fetches its own data the first time it is opened, and again
     whenever its inputs change — a page nobody has visited costs nothing. */
  useEffect(() => {
    if (tab === "employees") loadEmployees();
  }, [tab, loadEmployees]);

  useEffect(() => {
    if (tab === "statutory") loadStatutory();
  }, [tab, loadStatutory]);

  useEffect(() => {
    if (tab === "register") loadRegister();
  }, [tab, loadRegister]);

  /* Master data, once. `settings` is read-only here — it is edited on the
     Finance Setup tab, and only for users who may actually change it. */
  useEffect(() => {
    financeService
      .getDepartments()
      .then(setDepartments)
      .catch(() => setDepartments([]));
    financeService
      .getBankAccounts()
      .then((rows) => setBanks(rows.filter((b) => b.is_active && b.type !== "card")))
      .catch(() => setBanks([]));
    financeService
      .getPayrollSettings()
      .then(setSettings)
      .catch(() => setSettings(null));
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const run = await financeService.getPayrollRun(id);
      setDetail(run);
      setDraftLines(run.lines.map((l) => ({ staff_id: l.staff_id, allowances: l.allowances })));
    } catch (e: any) {
      fail(e, "Failed to load payroll run");
    } finally {
      setDetailLoading(false);
    }
  };

  const reloadDetail = async (id: string) => {
    try {
      const run = await financeService.getPayrollRun(id);
      setDetail(run);
      setDraftLines(run.lines.map((l) => ({ staff_id: l.staff_id, allowances: l.allowances })));
    } catch {
      /* The run may have been removed; the list is refreshed regardless. */
    }
  };

  /** One place that refreshes both the list and whatever run is open. */
  const act = async (label: string, action: () => Promise<any>, runId?: string) => {
    setBusy(true);
    try {
      await action();
      message.success(label);
      await loadRuns();
      if (runId) await reloadDetail(runId);
    } catch (e: any) {
      fail(e, `${label} failed`);
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------------------
   * Creating a run
   * ------------------------------------------------------------ */

  const activeEmployees = employeeData?.employees.filter((e) => e.employment_status === "active") ?? [];
  const configured = activeEmployees.filter((e) => !isNullish(e.basic_salary));
  const missingSalary = activeEmployees.filter((e) => isNullish(e.basic_salary));

  /**
   * Open the builder with every configured employee ticked and each person's
   * saved profile allowance already in the box — the operator only touches
   * the month that differs.
   *
   * The employee list is fetched fresh when the page mount fetch failed or
   * has not landed yet: opening the modal against an empty roster would
   * disable the Build button with no visible reason.
   */
  const openCreate = async () => {
    createForm.setFieldsValue({
      period_month: dayjs(),
      business_date: dayjs(),
      payment_method: "credit",
    });
    let roster = employeeData;
    if (!roster) {
      try {
        roster = await financeService.getPayrollEmployees(taxYear);
        setEmployeeData(roster);
      } catch {
        message.error("Could not load the employee list — try again");
        return;
      }
    }
    const active = roster.employees.filter((e) => e.employment_status === "active");
    setSelectedStaff(
      active.filter((e) => !isNullish(e.basic_salary)).map((e) => e.id),
    );
    setRunAllowances(
      Object.fromEntries(active.map((e) => [e.id, e.default_allowances ?? 0])),
    );
    setCreateOpen(true);
  };

  /** Gross of the current selection — live totals under the employee table. */
  const selectedGross = activeEmployees
    .filter((e) => selectedStaff.includes(e.id) && !isNullish(e.basic_salary))
    .reduce(
      (sum, e) => sum + Number(e.basic_salary || 0) + Number(runAllowances[e.id] || 0),
      0,
    );

  const submitCreate = async () => {
    const values = await createForm.validateFields();
    if (!selectedStaff.length) {
      message.error("Select at least one employee to pay");
      return;
    }
    setCreating(true);
    try {
      const result = await financeService.createPayrollRun({
        period_month: values.period_month.format("YYYY-MM"),
        business_date: values.business_date.format("YYYY-MM-DD"),
        payment_method: values.payment_method,
        bank_account_id:
          values.payment_method === "credit" ? null : values.bank_account_id,
        remarks: values.remarks || null,
        // Named selection: only the ticked employees are built, each with
        // the allowance shown in the table. Unconfigured people cannot be
        // ticked at all, so they are never paid a silent zero.
        lines: selectedStaff.map((id) => ({
          staff_id: id,
          allowances: Number(runAllowances[id] || 0),
        })),
      });
      setCreateOpen(false);
      createForm.resetFields();
      await loadRuns();
      await openDetail(result.voucher.id);
      if (result.unconfigured.length) {
        Modal.warning({
          title: `${result.unconfigured.length} employee(s) were not paid`,
          content: (
            <div>
              <Text>
                Nobody has told the system what these people earn, so they were left out of the
                run rather than paid zero. Set a salary and rebuild the run.
              </Text>
              <ul style={{ marginTop: 8, paddingLeft: 18, marginBottom: 0 }}>
                {result.unconfigured.map((u) => (
                  <li key={u.staff_id}>{u.full_name}</li>
                ))}
              </ul>
            </div>
          ),
        });
      } else {
        message.success("Payroll run built");
      }
    } catch (e: any) {
      fail(e, "Failed to build payroll run");
    } finally {
      setCreating(false);
    }
  };

  const saveLines = async () => {
    if (!detail) return;
    await act(
      "Draft rebuilt",
      () =>
        financeService.updatePayrollRun(detail.id, {
          period_month: monthKey(detail.lines[0]?.period_month),
          lines: draftLines,
        }),
      detail.id,
    );
  };

  /* ------------------------------------------------------------
   * Actions
   * ------------------------------------------------------------ */

  const submitPay = async () => {
    if (!payFor || !payBank) return;
    await act(
      "Net pay settled and posted",
      () => financeService.payPayrollRun(payFor.id, payBank),
      payFor.id,
    );
    setPayFor(null);
    setPayBank(undefined);
  };

  const submitVoid = async () => {
    if (!voidFor) return;
    if (!voidReason.trim()) {
      message.error("A void reason is required");
      return;
    }
    const id = voidFor.id;
    await act(
      "Run voided — reversal posted and the month released",
      () => financeService.voidPayrollRun(id, voidReason.trim()),
      id,
    );
    setVoidFor(null);
    setVoidReason("");
  };

  /**
   * Pull the server-rendered payslip PDF and open it. The figures are the
   * stored line values, so what the employee reads is what the ledger holds.
   */
  const printPayslip = async (staffId?: string) => {
    if (!detail) return;
    setSlipBusy(true);
    try {
      const blob = await financeService.getPayslips(detail.id, staffId);
      const name = staffId
        ? `payslip-${detail.voucher_no}.pdf`
        : `payslips-${detail.voucher_no}.pdf`;
      const opened = financeService.openPdf(blob, `Payslip — ${detail.voucher_no}`);
      if (!opened) {
        financeService.downloadPdf(blob, name);
        message.info("Pop-up blocked — the payslip was downloaded instead");
      }
    } catch (e: any) {
      fail(e, "Failed to generate payslip");
    } finally {
      setSlipBusy(false);
    }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    if (!rejectNotes.trim()) {
      message.error("A rejection reason is required");
      return;
    }
    const id = rejectFor.id;
    await act(
      "Payroll run rejected",
      () => financeService.rejectPayrollRun(id, rejectNotes.trim()),
      id,
    );
    setRejectFor(null);
    setRejectNotes("");
  };

  const submitRemit = async () => {
    if (!remitFor) return;
    const values = await remitForm.validateFields();
    setRemitting(true);
    try {
      await financeService.remitStatutory({
        kind: remitFor,
        amount: values.amount,
        business_date: values.business_date.format("YYYY-MM-DD"),
        bank_account_id: values.bank_account_id,
        memo: values.memo || undefined,
      });
      message.success("Statutory remittance posted");
      setRemitFor(null);
      remitForm.resetFields();
      await loadStatutory();
    } catch (e: any) {
      fail(e, "Failed to record the remittance");
    } finally {
      setRemitting(false);
    }
  };

  const saveProfile = async () => {
    if (!profileFor) return;
    const values = await profileForm.validateFields();
    setSavingProfile(true);
    try {
      await financeService.savePayrollEmployee(profileFor.id, {
        basic_salary: isNullish(values.basic_salary) ? null : values.basic_salary,
        default_allowances: isNullish(values.default_allowances)
          ? 0
          : values.default_allowances,
        finance_department_id: values.finance_department_id || null,
      });
      message.success("Payroll profile saved");
      setProfileFor(null);
      await loadEmployees();
      await loadRuns();
    } catch (e: any) {
      fail(e, "Failed to save the payroll profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveDeclaration = async () => {
    if (!profileFor) return;
    const touched = ["tax_year", "opening_income", "opening_tax"].some((f) =>
      profileForm.isFieldTouched(f),
    );
    if (!touched) {
      message.info("Nothing to save — the year-to-date declaration was not changed");
      return;
    }
    const values = await profileForm.validateFields(["tax_year", "opening_income", "opening_tax"]);
    setSavingProfile(true);
    try {
      await financeService.savePayrollEmployee(profileFor.id, {
        tax_year: dayjs(values.tax_year).year(),
        opening_income: values.opening_income || 0,
        opening_tax: values.opening_tax || 0,
      });
      message.success("Year-to-date declaration saved");
      setProfileFor(null);
      await loadEmployees();
    } catch (e: any) {
      fail(e, "Failed to save the declaration");
    } finally {
      setSavingProfile(false);
    }
  };

  /* ------------------------------------------------------------
   * Runs table
   * ------------------------------------------------------------ */

  const runActions = (row: PayrollRunDetail) => {
    const nodes: React.ReactNode[] = [];
    if (row.status === "draft" || row.status === "rejected") {
      if (canEdit) {
        nodes.push(
          <Button key="edit" size="small" onClick={() => openDetail(row.id)}>
            Edit
          </Button>,
          <Button
            key="submit"
            size="small"
            onClick={() => act("Submitted for approval", () => financeService.submitPayrollRun(row.id), row.id)}
          >
            Submit
          </Button>,
        );
      }
      if (canEdit && row.status === "draft") {
        nodes.push(
          <Popconfirm
            key="delete"
            title="Delete this draft run?"
            description="The lines are discarded. Nothing was ever posted."
            onConfirm={() =>
              act("Draft deleted", async () => {
                await financeService.deletePayrollRun(row.id);
                if (detail?.id === row.id) setDetail(null);
              })
            }
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>,
        );
      }
    }
    if (row.status === "pending_approval" && canApprove) {
      if (row.created_by && row.created_by === myId) {
        // The server answers `self_approval` with a 403, so showing a button
        // that cannot work would only teach the operator to distrust it.
        nodes.push(
          <Text key="sod" type="secondary" style={{ fontSize: 12 }}>
            You created this run — somebody else must approve it
          </Text>,
        );
      } else {
        nodes.push(
          <Button
            key="approve"
            size="small"
            type="primary"
            loading={busy}
            onClick={() => act("Approved — payroll posted to the ledger", () => financeService.approvePayrollRun(row.id), row.id)}
          >
            Approve
          </Button>,
          <Button key="reject" size="small" onClick={() => setRejectFor(row)}>
            Reject
          </Button>,
        );
      }
    }
    if (row.status === "pending_approval" && canEdit) {
      nodes.push(
        <Button key="void" size="small" danger onClick={() => setVoidFor(row)}>
          Void
        </Button>,
      );
    }
    if (row.status === "approved" && canEdit) {
      nodes.push(
        <Button key="pay" size="small" type="primary" onClick={() => setPayFor(row)}>
          Pay
        </Button>,
        <Button key="void" size="small" danger onClick={() => setVoidFor(row)}>
          Void
        </Button>,
      );
    }
    if (row.status === "paid" && canEdit) {
      nodes.push(
        <Button key="void" size="small" danger onClick={() => setVoidFor(row)}>
          Void
        </Button>,
      );
    }
    return <Space size={4} wrap>{nodes}</Space>;
  };

  const runColumns = [
    {
      title: "Run",
      dataIndex: "voucher_no",
      key: "voucher_no",
      width: 140,
      render: (v: string, row: PayrollRunDetail) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => openDetail(row.id)}
        >
          {v}
        </Button>
      ),
    },
    {
      title: "Period",
      key: "period",
      width: 100,
      render: (_: any, row: PayrollRunDetail) => monthKey(row.lines[0]?.period_month) || "—",
    },
    {
      title: "Date",
      dataIndex: "business_date",
      key: "business_date",
      width: 115,
      render: (v: string) => dayjs(dateKey(v)).format("MMM D, YYYY"),
    },
    {
      title: "Staff",
      key: "staff",
      width: 70,
      align: "right" as const,
      render: (_: any, row: PayrollRunDetail) => row.totals.employee_count,
    },
    {
      title: "Gross",
      key: "gross",
      width: 125,
      align: "right" as const,
      render: (_: any, row: PayrollRunDetail) => formatLkr(row.totals.gross_amount),
    },
    {
      title: "Net pay",
      key: "net",
      width: 125,
      align: "right" as const,
      render: (_: any, row: PayrollRunDetail) => formatLkr(row.totals.net_pay),
    },
    {
      title: "Employer cost",
      dataIndex: "amount",
      key: "amount",
      width: 135,
      align: "right" as const,
      render: (v: number) => formatLkr(v),
    },
    {
      title: "Approvals",
      key: "approvals",
      width: 95,
      align: "right" as const,
      render: (_: any, row: PayrollRunDetail) => (
        <Text type={row.approvals_count >= row.approvals_required ? "success" : "secondary"}>
          {row.approvals_count}/{row.approvals_required}
        </Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (s: string) => <StatusTag status={s} />,
    },
    {
      title: "",
      key: "actions",
      width: 300,
      render: (_: any, row: PayrollRunDetail) => runActions(row),
    },
  ];

  /* ------------------------------------------------------------
   * Employees table
   * ------------------------------------------------------------ */

  const employeeColumns = [
    {
      title: "Employee",
      key: "name",
      width: 240,
      render: (_: any, row: PayrollEmployee) => (
        <div>
          <div>{row.full_name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.email}
          </Text>
        </div>
      ),
    },
    {
      title: "Resort department",
      dataIndex: "department",
      key: "department",
      width: 150,
      ellipsis: true,
      render: (v: string | null) => v || "—",
    },
    {
      title: "Status",
      dataIndex: "employment_status",
      key: "employment_status",
      width: 110,
      render: (v: string) =>
        v === "active" ? <Tag color="green">active</Tag> : <Tag>{v}</Tag>,
    },
    {
      title: "Basic salary",
      dataIndex: "basic_salary",
      key: "basic_salary",
      width: 160,
      render: (v: number | null) => <Salary value={v} />,
    },
    {
      title: "Allowances / month",
      dataIndex: "default_allowances",
      key: "default_allowances",
      width: 150,
      align: "right" as const,
      render: (v: number) => formatLkr(v ?? 0),
    },
    {
      title: "Cost lands in",
      key: "cost_department",
      width: 150,
      render: (_: any, row: PayrollEmployee) =>
        row.finance_department_code ? (
          <Tag color="blue">{row.finance_department_name}</Tag>
        ) : (
          <Tag title="No department mapping, so the build step attributes this person's cost to Labour">
            Labour (fallback)
          </Tag>
        ),
    },
    {
      title: `YTD income ${taxYear}`,
      key: "ytd_income",
      width: 140,
      align: "right" as const,
      render: (_: any, row: PayrollEmployee) => formatLkr(row.ytd_income),
    },
    {
      title: `YTD tax ${taxYear}`,
      key: "ytd_tax",
      width: 130,
      align: "right" as const,
      render: (_: any, row: PayrollEmployee) => formatLkr(row.ytd_tax),
    },
    {
      title: "",
      key: "actions",
      width: 90,
      render: (_: any, row: PayrollEmployee) =>
        canEdit ? (
          <Button
            size="small"
            onClick={() => setProfileFor(row)}
          >
            Edit
          </Button>
        ) : null,
    },
  ];

  /* ------------------------------------------------------------
   * Register table
   * ------------------------------------------------------------ */

  const registerColumns = [
    { title: "Employee", dataIndex: "employee_name", key: "employee_name", width: 190, fixed: "left" as const },
    {
      title: "Period",
      dataIndex: "period_month",
      key: "period_month",
      width: 100,
      render: (v: string) => monthKey(v),
    },
    { title: "Run", dataIndex: "voucher_no", key: "voucher_no", width: 130 },
    {
      title: "Date",
      dataIndex: "business_date",
      key: "business_date",
      width: 110,
      render: (v: string) => dayjs(dateKey(v)).format("MMM D, YYYY"),
    },
    {
      title: "Department",
      dataIndex: "department_name",
      key: "department_name",
      width: 140,
      ellipsis: true,
      render: (v: string | null) => v || "—",
    },
    { title: "Basic", dataIndex: "basic_salary", key: "basic_salary", width: 115, align: "right" as const, render: (v: number) => formatLkr(v) },
    { title: "Allowances", dataIndex: "allowances", key: "allowances", width: 115, align: "right" as const, render: (v: number) => formatLkr(v) },
    { title: "Gross", dataIndex: "gross_amount", key: "gross_amount", width: 115, align: "right" as const, render: (v: number) => formatLkr(v) },
    { title: "EE EPF", dataIndex: "epf_employee", key: "epf_employee", width: 105, align: "right" as const, render: (v: number) => formatLkr(v) },
    { title: "ER EPF", dataIndex: "epf_employer", key: "epf_employer", width: 105, align: "right" as const, render: (v: number) => formatLkr(v) },
    { title: "ETF", dataIndex: "etf_employer", key: "etf_employer", width: 100, align: "right" as const, render: (v: number) => formatLkr(v) },
    { title: "EPF/ETF", dataIndex: "epf_etf", key: "epf_etf", width: 115, align: "right" as const, render: (v: number) => formatLkr(v) },
    { title: "APIT", dataIndex: "apit", key: "apit", width: 105, align: "right" as const, render: (v: number) => formatLkr(v) },
    { title: "Net pay", dataIndex: "net_pay", key: "net_pay", width: 120, align: "right" as const, render: (v: number) => formatLkr(v) },
    { title: "Employer cost", dataIndex: "employer_cost", key: "employer_cost", width: 135, align: "right" as const, render: (v: number) => formatLkr(v) },
    { title: "Status", dataIndex: "status", key: "status", width: 130, render: (v: string) => <StatusTag status={v} /> },
  ];

  /* ------------------------------------------------------------
   * Tabs
   * ------------------------------------------------------------ */

  const runsTab = (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        {canEdit && (
          <Button
            type="primary"
            onClick={openCreate}
          >
            New payroll run
          </Button>
        )}
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 170 }}
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          options={[
            { value: "draft", label: "Draft" },
            { value: "pending_approval", label: "Pending approval" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
            { value: "paid", label: "Paid" },
            { value: "void", label: "Voided" },
          ]}
        />
        <RangePicker
          value={range}
          onChange={(v) => {
            setRange(v as [Dayjs, Dayjs] | null);
            setPage(1);
          }}
        />
        <Input.Search
          allowClear
          placeholder="Run no / description"
          style={{ width: 220 }}
          onSearch={(v) => {
            setQuery(v);
            setPage(1);
          }}
        />
      </Space>

      {runsError && (
        <Alert type="error" showIcon title="Failed to load" description={runsError} style={{ marginBottom: 16 }} />
      )}

      <Table
        rowKey="id"
        size="small"
        loading={runsLoading}
        dataSource={runs?.rows || []}
        columns={runColumns}
        scroll={{ x: 1400 }}
        pagination={{
          current: page,
          total: runs?.total || 0,
          pageSize: 20,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
        }}
      />
    </>
  );

  const employeesTab = (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <Text type="secondary">Tax year</Text>
        <DatePicker
          picker="year"
          value={dayjs().year(taxYear)}
          onChange={(v) => v && setTaxYear(v.year())}
          allowClear={false}
        />
        <Text type="secondary">
          {configured.length} configured · {missingSalary.length} without a salary
        </Text>
      </Space>

      {employeesError && (
        <Alert type="error" showIcon title="Failed to load" description={employeesError} style={{ marginBottom: 16 }} />
      )}

      <Alert
        type="info"
        showIcon
        title="A blank salary is not a zero salary"
        description="Anyone without a configured basic salary is left out of a run and named afterwards. Mapping a person to a department decides which operating department their cost lands in; with no mapping it falls back to Labour."
        style={{ marginBottom: 16 }}
      />

      <Table
        rowKey="id"
        size="small"
        loading={employeesLoading}
        dataSource={employeeData?.employees || []}
        columns={employeeColumns}
        scroll={{ x: 1250 }}
        pagination={false}
      />
    </>
  );

  const statutoryTab = (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statutoryLoading}>
            <Statistic
              title="EPF / ETF outstanding"
              value={statutory?.outstanding.epf_etf ?? 0}
              precision={2}
              suffix="LKR"
              valueStyle={
                (statutory?.outstanding.epf_etf ?? 0) > 0 ? { color: "#d46b08" } : undefined
              }
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              what 2030 still owes the boards
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statutoryLoading}>
            <Statistic
              title="APIT outstanding"
              value={statutory?.outstanding.apit ?? 0}
              precision={2}
              suffix="LKR"
              valueStyle={(statutory?.outstanding.apit ?? 0) > 0 ? { color: "#cf1322" } : undefined}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              withheld from employees, owed to the IRD
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statutoryLoading}>
            <Statistic
              title="Employee EPF"
              value={settings ? settings.epf_employee_rate * 100 : 0}
              precision={2}
              suffix="%"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              deducted from gross pay
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={statutoryLoading}>
            <Statistic
              title="Employer EPF + ETF"
              value={settings ? (settings.epf_employer_rate + settings.etf_employer_rate) * 100 : 0}
              precision={2}
              suffix="%"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              on top of gross pay — part of the employer cost
            </Text>
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 12 }} wrap>
        <Button
          type="primary"
          disabled={!canEdit || !statutory || statutory.outstanding.epf_etf <= 0}
          onClick={() => {
            remitForm.setFieldsValue({
              amount: statutory?.outstanding.epf_etf,
              business_date: dayjs(),
              bank_account_id: banks[0]?.id,
            });
            setRemitFor("epf_etf");
          }}
        >
          Remit EPF / ETF
        </Button>
        <Button
          type="primary"
          disabled={!canEdit || !statutory || statutory.outstanding.apit <= 0}
          onClick={() => {
            remitForm.setFieldsValue({
              amount: statutory?.outstanding.apit,
              business_date: dayjs(),
              bank_account_id: banks[0]?.id,
            });
            setRemitFor("apit");
          }}
        >
          Remit APIT
        </Button>
        <Text type="secondary">As of {statutory?.as_of || "—"}</Text>
      </Space>

      {statutoryError && (
        <Alert type="error" showIcon title="Failed to load" description={statutoryError} style={{ marginBottom: 16 }} />
      )}

      <Card title="Remittances to the boards" size="small" style={{ marginBottom: 16 }}>
        <Table
          rowKey="id"
          size="small"
          loading={statutoryLoading}
          dataSource={statutory?.remittances || []}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          columns={[
            {
              title: "Date",
              dataIndex: "business_date",
              width: 120,
              render: (v: string) => dayjs(dateKey(v)).format("MMM D, YYYY"),
            },
            {
              title: "Kind",
              dataIndex: "kind",
              width: 140,
              render: (v: RemittanceKind) =>
                v === "apit" ? <Tag color="red">APIT</Tag> : <Tag color="blue">EPF / ETF</Tag>,
            },
            {
              title: "Amount",
              dataIndex: "amount",
              width: 150,
              align: "right" as const,
              render: (v: number) => formatLkr(v),
            },
            { title: "From", dataIndex: "bank_account_name", width: 170, ellipsis: true },
            { title: "Memo", dataIndex: "memo", ellipsis: true },
            { title: "By", dataIndex: "created_by_name", width: 150, ellipsis: true, render: (v: string | null) => v || "—" },
          ]}
        />
      </Card>

      <Card title="IRD Tax Table No. 1 — APIT bands" size="small">
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          APIT is withheld on the CUMULATIVE year, so a month near the end of the year is
          compared against everything already earned. Rates and thresholds are edited on
          Finance Setup.
        </Text>
        <Table
          rowKey={(b: any) => `${b.from}-${b.max ?? "open"}`}
          size="small"
          loading={statutoryLoading}
          dataSource={settings?.apit_bands || []}
          pagination={false}
          columns={[
            {
              title: "From",
              dataIndex: "from",
              width: 140,
              align: "right" as const,
              render: (v: number) => formatLkr(v),
            },
            {
              title: "Up to",
              dataIndex: "max",
              width: 140,
              align: "right" as const,
              render: (v: number | null) => (v === null ? <Tag>open-ended</Tag> : formatLkr(v)),
            },
            {
              title: "Rate",
              dataIndex: "rate",
              width: 100,
              align: "right" as const,
              render: (v: number) => `${(v * 100).toFixed(0)}%`,
            },
            {
              title: "Constant",
              dataIndex: "constant",
              align: "right" as const,
              render: (v: number) => formatLkr(v),
            },
          ]}
        />
      </Card>
    </>
  );

  const registerTab = (
    <>
      <Space style={{ marginBottom: 12 }} wrap>
        <RangePicker
          value={regRange}
          allowClear={false}
          onChange={(v) => v && setRegRange(v as [Dayjs, Dayjs])}
        />
        <Text type="secondary">
          {register ? `${register.summary.line_count} line(s), ${register.summary.employees} employee(s), ${register.summary.runs} run(s)` : ""}
        </Text>
      </Space>

      {registerError && (
        <Alert type="error" showIcon title="Failed to load" description={registerError} style={{ marginBottom: 16 }} />
      )}

      {register && (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Employer cost" value={register.summary.employer_cost} precision={2} suffix="LKR" />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  debited to 6100 Salaries &amp; Wages
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="EPF / ETF withheld + contributed" value={register.summary.epf_etf} precision={2} suffix="LKR" />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  credited to 2030
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="APIT withheld" value={register.summary.apit} precision={2} suffix="LKR" />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  credited to 2040
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic title="Net pay" value={register.summary.net_pay} precision={2} suffix="LKR" />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  what actually reaches the employees
                </Text>
              </Card>
            </Col>
          </Row>

          <Alert
            type={register.control.matched ? "success" : "error"}
            showIcon
            title={
              register.control.matched
                ? "Control matched — the register agrees with the ledger"
                : `Control variance of ${register.control.variance}`
            }
            description={
              register.control.matched
                ? `Source employer cost ${formatLkr(register.control.source_employer_cost)} against ${formatLkr(register.control.gl_wages)} debited to 6100.`
                : `Source employer cost ${formatLkr(register.control.source_employer_cost)} against ${formatLkr(register.control.gl_wages)} debited to 6100.`
            }
            style={{ marginBottom: 16 }}
          />
        </>
      )}

      <Table
        rowKey={(r: any) => `${r.voucher_id}-${r.employee_name}-${r.period_month}`}
        size="small"
        loading={registerLoading}
        dataSource={register?.rows || []}
        columns={registerColumns}
        scroll={{ x: 2100 }}
        pagination={{ pageSize: 25, showSizeChanger: false }}
      />
    </>
  );

  const tabItems = [
    { key: "runs", label: "Runs", children: runsTab },
    { key: "employees", label: "Employees", children: employeesTab },
    { key: "statutory", label: "Statutory", children: statutoryTab },
    { key: "register", label: "Register", children: registerTab },
  ];

  /* ------------------------------------------------------------
   * Render
   * ------------------------------------------------------------ */

  const draftDirty =
    !!detail &&
    detail.lines.length === draftLines.length &&
    detail.lines.some((l, i) => Number(l.allowances) !== Number(draftLines[i]?.allowances));

  /** A payslip promises approved money — drafts and voided runs have none. */
  const payslipsAvailable =
    !!detail && (detail.status === "approved" || detail.status === "paid");

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }} wrap>
        <div>
          <Title level={4} style={{ marginBottom: 4 }}>
            Payroll
          </Title>
          <Text type="secondary">
            Salaries, statutory withholdings and what they leave the resort owing
          </Text>
        </div>
      </Space>

      <Tabs activeKey={tab} onChange={setTab} items={tabItems} />

      {/* ---------------- Build a run ---------------- */}
      <Modal
        title="New payroll run"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={submitCreate}
        okText="Build run"
        confirmLoading={creating}
        okButtonProps={{ disabled: selectedStaff.length === 0 }}
        width={860}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Alert
            type={missingSalary.length ? "warning" : "info"}
            showIcon
            title={`${selectedStaff.length} of ${configured.length} employee(s) will be paid`}
            description={
              missingSalary.length ? (
                <>
                  {missingSalary.length} active employee(s) have no configured salary and cannot be
                  selected until one is set: {missingSalary.map((e) => e.full_name).join(", ")}.
                </>
              ) : (
                "Every active employee has a configured salary."
              )
            }
            action={
              missingSalary.length ? (
                <Button
                  size="small"
                  onClick={() => {
                    setCreateOpen(false);
                    setTab("employees");
                  }}
                >
                  Configure salaries
                </Button>
              ) : undefined
            }
            style={{ marginBottom: 16 }}
          />

          <Title level={5} style={{ marginBottom: 8 }}>
            Employees to pay
          </Title>
          <Table
            rowKey="id"
            size="small"
            style={{ marginBottom: 12 }}
            pagination={false}
            scroll={{ y: 260 }}
            dataSource={activeEmployees}
            rowSelection={{
              selectedRowKeys: selectedStaff,
              onChange: (keys) => setSelectedStaff(keys as string[]),
              getCheckboxProps: (row: PayrollEmployee) => ({
                // An unconfigured salary is a question, not a zero: the box
                // stays off until somebody answers it.
                disabled: isNullish(row.basic_salary),
              }),
            }}
            columns={[
              {
                title: "Employee",
                key: "name",
                render: (_: any, row: PayrollEmployee) => (
                  <div>
                    <div>{row.full_name}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {row.department || "—"}
                    </Text>
                  </div>
                ),
              },
              {
                title: "Basic salary",
                key: "basic",
                width: 140,
                align: "right" as const,
                render: (_: any, row: PayrollEmployee) => <Salary value={row.basic_salary} />,
              },
              {
                title: "Allowances",
                key: "allowances",
                width: 150,
                render: (_: any, row: PayrollEmployee) => (
                  <InputNumber
                    size="small"
                    style={{ width: "100%" }}
                    min={0}
                    disabled={isNullish(row.basic_salary) || !selectedStaff.includes(row.id)}
                    value={runAllowances[row.id] ?? 0}
                    onChange={(v) =>
                      setRunAllowances((prev) => ({
                        ...prev,
                        [row.id]: Number(v) || 0,
                      }))
                    }
                  />
                ),
              },
              {
                title: "Status",
                key: "state",
                width: 130,
                render: (_: any, row: PayrollEmployee) =>
                  isNullish(row.basic_salary) ? (
                    <Tag>Not configured</Tag>
                  ) : selectedStaff.includes(row.id) ? (
                    <Tag color="green">will be paid</Tag>
                  ) : (
                    <Tag>excluded</Tag>
                  ),
              },
            ]}
          />
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 16 }}>
            Selection gross: {formatLkr(selectedGross)} before EPF and APIT. Allowances shown here
            come from each employee's saved profile and can be changed for this month only.
          </Text>

          <Form.Item
            name="period_month"
            label="Pay period"
            rules={[{ required: true, message: "Choose the month being paid" }]}
          >
            <DatePicker picker="month" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="business_date"
            label="Business date"
            rules={[{ required: true, message: "Choose the business date" }]}
          >
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(d) => d.isAfter(dayjs(), "day")}
            />
          </Form.Item>
          <Form.Item
            name="payment_method"
            label="Payment"
            rules={[{ required: true }]}
          >
            <Select options={PAYMENT_OPTIONS} />
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.payment_method !== c.payment_method}>
            {({ getFieldValue }) =>
              getFieldValue("payment_method") !== "credit" ? (
                <Form.Item
                  name="bank_account_id"
                  label="From account"
                  rules={[{ required: true, message: "Choose the account the pay leaves from" }]}
                >
                  <Select
                    placeholder="Select an account"
                    options={banks.map((b) => ({ value: b.id, label: b.name }))}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            On credit, the net pay is credited to 2010 (Amounts payable) when the run is
            approved and leaves the bank when you settle it — so the employer's own EPF/ETF
            contribution is never drawn twice.
          </Text>
        </Form>
      </Modal>

      {/* ---------------- Run detail ---------------- */}
      <Modal
        title={detail ? `Payroll run ${detail.voucher_no}` : "Payroll run"}
        open={!!detail || detailLoading}
        onCancel={() => setDetail(null)}
        width={1180}
        footer={null}
        destroyOnClose
      >
        {detailLoading && !detail ? (
          <Empty description="Loading…" />
        ) : detail ? (
          <>
            <Descriptions
              size="small"
              column={4}
              bordered
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Status">
                <StatusTag status={detail.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Period">
                {monthKey(detail.lines[0]?.period_month) || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Business date">
                {dayjs(dateKey(detail.business_date)).format("MMM D, YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label="Payment">
                {detail.payment_method === "credit"
                  ? "On credit (2010)"
                  : `${detail.payment_method} · ${detail.bank_account_name || "—"}`}
              </Descriptions.Item>
              <Descriptions.Item label="Approvals">
                {detail.approvals_count}/{detail.approvals_required}
              </Descriptions.Item>
              <Descriptions.Item label="Department">{detail.department_name}</Descriptions.Item>
              <Descriptions.Item label="Created by">
                {detail.created_by_name || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Journal">
                {detail.journal_id ? <Tag color="green">posted</Tag> : <Tag>not posted</Tag>}
              </Descriptions.Item>
            </Descriptions>

            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={8} md={4}>
                <Statistic title="Staff" value={detail.totals.employee_count} />
              </Col>
              <Col xs={12} sm={8} md={5}>
                <Statistic title="Gross" value={detail.totals.gross_amount} precision={2} suffix="LKR" />
              </Col>
              <Col xs={12} sm={8} md={5}>
                <Statistic title="EPF / ETF" value={detail.totals.epf_etf_total} precision={2} suffix="LKR" />
              </Col>
              <Col xs={12} sm={8} md={4}>
                <Statistic title="APIT" value={detail.totals.apit} precision={2} suffix="LKR" />
              </Col>
              <Col xs={12} sm={8} md={5}>
                <Statistic title="Net pay" value={detail.totals.net_pay} precision={2} suffix="LKR" />
              </Col>
              <Col xs={12} sm={8} md={7}>
                <Statistic
                  title="Employer cost"
                  value={detail.totals.employer_cost}
                  precision={2}
                  suffix="LKR"
                  valueStyle={{ color: "#cf1322" }}
                />
              </Col>
            </Row>

            <Title level={5}>Employees</Title>
            {detail.status === "draft" && canEdit && (
              <Alert
                type="info"
                showIcon
                title="Allowances can be edited on a draft"
                description="Rebuilding recalculates every withholding and the header total from the lines, so the voucher always equals what the ledger will debit."
                style={{ marginBottom: 12 }}
              />
            )}
            <Table
              rowKey="staff_id"
              size="small"
              dataSource={detail.lines}
              pagination={false}
              scroll={{ x: 1250 }}
              style={{ marginBottom: 16 }}
              columns={[
                { title: "Employee", dataIndex: "employee_name", width: 180, fixed: "left" as const },
                {
                  title: "Period",
                  dataIndex: "period_month",
                  width: 95,
                  render: (v: string) => monthKey(v),
                },
                { title: "Department", dataIndex: "department_name", width: 140, ellipsis: true },
                { title: "Basic", dataIndex: "basic_salary", width: 110, align: "right" as const, render: (v: number) => formatLkr(v) },
                {
                  title: "Allowances",
                  key: "allowances",
                  width: 130,
                  align: "right" as const,
                  render: (_: any, line: PayrollLine) =>
                    detail.status === "draft" && canEdit ? (
                      <InputNumber
                        size="small"
                        min={0}
                        value={draftLines.find((d) => d.staff_id === line.staff_id)?.allowances ?? line.allowances}
                        onChange={(v) =>
                          setDraftLines((prev) =>
                            prev.map((d) =>
                              d.staff_id === line.staff_id ? { ...d, allowances: Number(v) || 0 } : d,
                            ),
                          )
                        }
                      />
                    ) : (
                      formatLkr(line.allowances)
                    ),
                },
                { title: "Gross", dataIndex: "gross_amount", width: 110, align: "right" as const, render: (v: number) => formatLkr(v) },
                { title: "EE EPF", dataIndex: "epf_employee", width: 100, align: "right" as const, render: (v: number) => formatLkr(v) },
                { title: "ER EPF", dataIndex: "epf_employer", width: 100, align: "right" as const, render: (v: number) => formatLkr(v) },
                { title: "ETF", dataIndex: "etf_employer", width: 95, align: "right" as const, render: (v: number) => formatLkr(v) },
                { title: "APIT", dataIndex: "apit", width: 100, align: "right" as const, render: (v: number) => formatLkr(v) },
                { title: "Net pay", dataIndex: "net_pay", width: 115, align: "right" as const, render: (v: number) => formatLkr(v) },
                { title: "Employer cost", dataIndex: "employer_cost", width: 130, align: "right" as const, render: (v: number) => formatLkr(v) },
                {
                  title: "",
                  key: "payslip",
                  width: 90,
                  render: (_: any, line: PayrollLine) =>
                    payslipsAvailable ? (
                      <Button
                        size="small"
                        loading={slipBusy}
                        onClick={() => printPayslip(line.staff_id)}
                      >
                        Payslip
                      </Button>
                    ) : null,
                },
              ]}
            />

            {detail.status === "draft" && canEdit && (
              <Space style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  loading={busy}
                  disabled={!draftDirty}
                  onClick={saveLines}
                >
                  Rebuild draft
                </Button>
                <Text type="secondary">
                  Header total follows the lines automatically.
                </Text>
              </Space>
            )}

            {detail.journal_lines && detail.journal_lines.length > 0 && (
              <>
                <Title level={5}>Journal</Title>
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={detail.journal_lines}
                  pagination={false}
                  scroll={{ x: 900 }}
                  style={{ marginBottom: 16 }}
                  columns={[
                    { title: "Account", dataIndex: "account_code", width: 90, render: (v: string) => <Text code>{v}</Text> },
                    { title: "Name", dataIndex: "account_name", ellipsis: true },
                    { title: "Department", dataIndex: "department_name", width: 150, ellipsis: true },
                    {
                      title: "Side",
                      dataIndex: "side",
                      width: 80,
                      render: (v: string) => (v === "debit" ? <Tag color="geekblue">debit</Tag> : <Tag color="volcano">credit</Tag>),
                    },
                    { title: "Amount", dataIndex: "amount", width: 130, align: "right" as const, render: (v: number) => formatLkr(v) },
                    { title: "Memo", dataIndex: "memo", ellipsis: true },
                  ]}
                />
              </>
            )}

            <Space wrap>
              {payslipsAvailable && (
                <Button loading={slipBusy} onClick={() => printPayslip()}>
                  Download payslips
                </Button>
              )}
              {(detail.status === "draft" || detail.status === "rejected") && canEdit && (
                <Button
                  loading={busy}
                  onClick={() => act("Submitted for approval", () => financeService.submitPayrollRun(detail.id), detail.id)}
                >
                  Submit for approval
                </Button>
              )}
              {detail.status === "pending_approval" && canApprove && (
                <>
                  {detail.created_by && detail.created_by === myId ? (
                    <Text type="secondary">
                      You created this run — somebody else must approve it.
                    </Text>
                  ) : (
                    <Button
                      type="primary"
                      loading={busy}
                      onClick={() => act("Approved — payroll posted to the ledger", () => financeService.approvePayrollRun(detail.id), detail.id)}
                    >
                      Approve
                    </Button>
                  )}
                  <Button onClick={() => setRejectFor(detail)}>Reject</Button>
                </>
              )}
              {detail.status === "pending_approval" && canEdit && (
                <Button danger onClick={() => setVoidFor(detail)}>
                  Void
                </Button>
              )}
              {detail.status === "approved" && canEdit && (
                <>
                  <Button
                    type="primary"
                    onClick={() => {
                      setPayBank(detail.bank_account_id || banks[0]?.id);
                      setPayFor(detail);
                    }}
                  >
                    Settle net pay
                  </Button>
                  <Button danger onClick={() => setVoidFor(detail)}>
                    Void
                  </Button>
                </>
              )}
              {detail.status === "paid" && canEdit && (
                <Button danger onClick={() => setVoidFor(detail)}>
                  Void
                </Button>
              )}
            </Space>
          </>
        ) : null}
      </Modal>

      {/* ---------------- Settle net pay ---------------- */}
      <Modal
        title="Settle net pay"
        open={!!payFor}
        onCancel={() => setPayFor(null)}
        onOk={submitPay}
        okText="Settle"
        confirmLoading={busy}
        okButtonProps={{ disabled: !payBank }}
        destroyOnClose
      >
        {payFor && (
          <Form layout="vertical">
            <Alert
              type="info"
              showIcon
              title={`Settling ${formatLkr(payFor.totals.net_pay)}`}
              description="The run debits 2010 (what the employees are owed) and credits this account. The employer's EPF/ETF contribution already left when the run was approved and is not drawn again."
              style={{ marginBottom: 16 }}
            />
            <Form.Item label="From account" required>
              <Select
                value={payBank}
                onChange={setPayBank}
                placeholder="Select an account"
                options={banks.map((b) => ({ value: b.id, label: b.name }))}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* ---------------- Void ---------------- */}
      <Modal
        title="Void payroll run"
        open={!!voidFor}
        onCancel={() => setVoidFor(null)}
        onOk={submitVoid}
        okText="Void run"
        okButtonProps={{ danger: true, disabled: !voidReason.trim() }}
        confirmLoading={busy}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          title="A reversal will be posted"
          description="The recognition journal (and any settlement) is reversed, the statutory liabilities unwind with it, and the month becomes payable again."
          style={{ marginBottom: 16 }}
        />
        <Form layout="vertical">
          <Form.Item label="Reason" required>
            <Input.TextArea
              rows={3}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              maxLength={500}
              placeholder="Why is this run being reversed?"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---------------- Reject ---------------- */}
      <Modal
        title="Reject payroll run"
        open={!!rejectFor}
        onCancel={() => setRejectFor(null)}
        onOk={submitReject}
        okText="Reject"
        okButtonProps={{ danger: true, disabled: !rejectNotes.trim() }}
        confirmLoading={busy}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="Reason" required>
            <Input.TextArea
              rows={3}
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              maxLength={500}
              placeholder="Why is this run being sent back?"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ---------------- Employee profile ---------------- */}
      <Modal
        title={profileFor ? `Payroll profile — ${profileFor.full_name}` : "Payroll profile"}
        open={!!profileFor}
        onCancel={() => setProfileFor(null)}
        onOk={saveProfile}
        okText="Save profile"
        confirmLoading={savingProfile}
        width={640}
        destroyOnClose
        footer={
          <Space>
            <Button onClick={() => setProfileFor(null)}>Cancel</Button>
            <Button onClick={saveDeclaration} loading={savingProfile}>
              Save year-to-date declaration
            </Button>
            <Button type="primary" onClick={saveProfile} loading={savingProfile}>
              Save profile
            </Button>
          </Space>
        }
      >
        <Form
          key={profileFor?.id || "new"}
          form={profileForm}
          layout="vertical"
          initialValues={
            profileFor
              ? {
                  basic_salary: profileFor.basic_salary,
                  default_allowances: profileFor.default_allowances ?? 0,
                  finance_department_id: profileFor.finance_department_id,
                  tax_year: dayjs().year(taxYear),
                  opening_income: profileFor.opening_income,
                  opening_tax: profileFor.opening_tax,
                }
              : {}
          }
        >
          <Form.Item
            name="basic_salary"
            label="Basic salary (monthly)"
            extra="Leave empty while it is unknown — the system will leave this person out of a run rather than pay zero."
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              placeholder="Not configured"
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            />
          </Form.Item>
          <Form.Item
            name="default_allowances"
            label="Allowances (monthly)"
            extra="Seeded into every new payroll run — transport, housing, anything recurring. Override it on the run for the month it differs."
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              defaultValue={0}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            />
          </Form.Item>
          <Form.Item
            name="finance_department_id"
            label="Cost lands in"
            extra="Which operating department carries this person's employer cost. With no mapping it falls back to Labour."
          >
            <Select
              allowClear
              placeholder="Labour (fallback)"
              options={departments.map((d) => ({ value: d.id, label: `${d.name} (${d.code})` }))}
            />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            title="IRD year-to-date declaration"
            description="APIT is computed on the cumulative year. Declaring the income already earned and the tax already withheld lets the very first run of the year withhold correctly."
            style={{ marginBottom: 16 }}
          />
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="tax_year" label="Tax year">
                <DatePicker picker="year" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="opening_income" label="Income already earned">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="opening_tax" label="Tax already withheld">
                <InputNumber style={{ width: "100%" }} min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* ---------------- Statutory remittance ---------------- */}
      <Modal
        title={remitFor === "apit" ? "Remit APIT" : "Remit EPF / ETF"}
        open={!!remitFor}
        onCancel={() => setRemitFor(null)}
        onOk={submitRemit}
        okText="Post remittance"
        confirmLoading={remitting}
        width={520}
        destroyOnClose
      >
        <Alert
          type="warning"
          showIcon
          title="Only what is owed can be remitted"
          description="Posting more than the outstanding liability is refused, so 2030 and 2040 can never go negative."
          style={{ marginBottom: 16 }}
        />
        <Form form={remitForm} layout="vertical">
          <Form.Item
            name="amount"
            label="Amount"
            rules={[
              { required: true, message: "Enter the amount being sent" },
              { type: "number", min: 0.01, message: "Amount must be greater than zero" },
            ]}
          >
            <InputNumber style={{ width: "100%" }} min={0} step={100} />
          </Form.Item>
          <Form.Item
            name="business_date"
            label="Business date"
            rules={[{ required: true, message: "Choose the business date" }]}
          >
            <DatePicker style={{ width: "100%" }} disabledDate={(d) => d.isAfter(dayjs(), "day")} />
          </Form.Item>
          <Form.Item
            name="bank_account_id"
            label="Leaves from"
            rules={[{ required: true, message: "Choose the account" }]}
          >
            <Select
              placeholder="Select an account"
              options={banks.map((b) => ({ value: b.id, label: b.name }))}
            />
          </Form.Item>
          <Form.Item name="memo" label="Memo">
            <Input maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PayrollPage;
